"use client"

import React, { useMemo, useState, useEffect } from "react"
import { motion, AnimatePresence, useAnimationControls } from "framer-motion"
import { cn } from "@/lib/utils"

type AnimationPhase = "idle" | "forming_column" | "scrolling_down" | "resetting"

type AnimatedListProps = {
  children: React.ReactNode
  className?: string
  stackGap?: number
  columnGap?: number
  scaleFactor?: number
  scrollDownDuration?: number
  formationDuration?: number
}

type InternalItemProps = {
  children: React.ReactNode
  className?: string
  index: number
  listLength: number
  animationPhase: AnimationPhase
  onFormationComplete?: () => void
  stackGap: number
  columnGap: number
  scaleFactor: number
  formationDuration: number
  visibleItemsCount: number
  resetSpringStiffness: number
  resetSpringDamping: number
}

function InternalAnimatedListItem({
  children,
  className,
  index,
  listLength,
  animationPhase,
  onFormationComplete,
  stackGap,
  columnGap,
  scaleFactor,
  formationDuration,
  visibleItemsCount,
  resetSpringStiffness,
  resetSpringDamping,
}: InternalItemProps) {
  const reverseIndex = listLength - 1 - index
  const isVisible = reverseIndex < visibleItemsCount
  const lastItemOffset = (listLength - 1) * columnGap
  const isLastItem = index === listLength - 1

  const itemVariants = {
    initial: {
      scale: 1 + index * scaleFactor,
      y: reverseIndex * stackGap,
      opacity: isVisible ? 1 : 0,
    },
    column: {
      scale: 1,
      y: index * columnGap - lastItemOffset,
      opacity: 1,
    },
  }

  const target =
    animationPhase === "idle" || animationPhase === "resetting" ? "initial" : "column"

  const getTransition = (): import("framer-motion").Transition => {
    if (animationPhase === "resetting") {
      return { type: "spring" as const, stiffness: resetSpringStiffness, damping: resetSpringDamping }
    }
    return { duration: formationDuration, ease: [0.4, 0, 0.2, 1] as [number, number, number, number] }
  }

  const handleAnimationComplete = (definition: string) => {
    if (isLastItem && definition === "column" && animationPhase === "forming_column") {
      onFormationComplete?.()
    }
  }

  return (
    <motion.div
      className={cn("w-full flex justify-center absolute inset-x-0", className)}
      variants={itemVariants}
      initial="initial"
      animate={target}
      transition={getTransition()}
      onAnimationComplete={handleAnimationComplete}
    >
      {children}
    </motion.div>
  )
}

export function AnimatedList({
  children,
  className,
  stackGap = 20,
  columnGap = 85,
  scaleFactor = 0.05,
  scrollDownDuration = 5,
  formationDuration = 1,
}: AnimatedListProps) {
  const loopPauseDuration = 100
  const listResetSpringStiffness = 100
  const listResetSpringDamping = 25
  const itemResetSpringStiffness = 120
  const itemResetSpringDamping = 20
  const visibleItemsCount = 4

  const [animationPhase, setAnimationPhase] = useState<AnimationPhase>("idle")
  const listControls = useAnimationControls()
  const childrenArray = useMemo(() => React.Children.toArray(children), [children])
  const listLength = childrenArray.length
  const totalHeight = listLength * columnGap

  useEffect(() => {
    if (animationPhase !== "idle") return
    const timer = setTimeout(() => setAnimationPhase("forming_column"), loopPauseDuration)
    return () => clearTimeout(timer)
  }, [animationPhase])

  useEffect(() => {
    if (animationPhase === "scrolling_down") {
      listControls.start({ y: totalHeight, transition: { duration: scrollDownDuration, ease: [0.4, 0, 0.2, 1] } })
    } else if (animationPhase === "resetting") {
      listControls.start({ y: 0, transition: { type: "spring", stiffness: listResetSpringStiffness, damping: listResetSpringDamping } })
    } else {
      listControls.set({ y: 0 })
    }
  }, [animationPhase, listControls, totalHeight, scrollDownDuration])

  const handleListAnimationComplete = (definition: { y?: number }) => {
    if (definition.y === totalHeight && animationPhase === "scrolling_down") {
      setAnimationPhase("resetting")
    } else if (definition.y === 0 && animationPhase === "resetting") {
      setAnimationPhase("idle")
    }
  }

  return (
    <motion.div
      className={cn("relative w-full h-full flex items-center", className)}
      initial={{ y: 0 }}
      animate={listControls}
      onAnimationComplete={handleListAnimationComplete}
    >
      <AnimatePresence>
        {childrenArray.map((child, index) => (
          <InternalAnimatedListItem
            key={index}
            index={index}
            listLength={listLength}
            animationPhase={animationPhase}
            onFormationComplete={
              index === listLength - 1 ? () => setAnimationPhase("scrolling_down") : undefined
            }
            stackGap={stackGap}
            columnGap={columnGap}
            scaleFactor={scaleFactor}
            formationDuration={formationDuration}
            visibleItemsCount={visibleItemsCount}
            resetSpringStiffness={itemResetSpringStiffness}
            resetSpringDamping={itemResetSpringDamping}
          >
            {child}
          </InternalAnimatedListItem>
        ))}
      </AnimatePresence>
    </motion.div>
  )
}
